/**
 * Posts Page - Create, edit, and manage LinkedIn post drafts
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../platform';
import { dedupeRequest, clearCacheKey } from '../lib/request-cache';
import {
  Edit2,
  Trash2,
  Copy,
  RefreshCw,
  CheckCircle,
  Send,
  Archive,
  Sparkles,
  X,
  History,
  Wand2,
  MoreVertical,
  Info,
  Linkedin,
  ArrowRight,
  ThumbsUp,
  MessageCircle,
  Repeat2,
  Globe,
  Loader2,
} from 'lucide-react';
import { api, streamPipelineEvents, type PostDraft, type Source, type WeeklyInsight as WeeklyInsightType, type LinkedInConnection, type PipelineProgress } from '../lib/api';
import { LoadingOverlay } from '../components/LoadingOverlay';



const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  idea: { label: 'Idea', color: 'bg-gray-100 text-gray-700', icon: <Sparkles className="h-3 w-3" /> },
  draft: { label: 'Draft', color: 'bg-yellow-100 text-yellow-700', icon: <Edit2 className="h-3 w-3" /> },
  ready_to_publish: { label: 'Ready to Publish', color: 'bg-mazo-100 text-mazo-700', icon: <CheckCircle className="h-3 w-3" /> },
  published: { label: 'Published', color: 'bg-mazo-100 text-mazo-700', icon: <Send className="h-3 w-3" /> },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-500', icon: <Archive className="h-3 w-3" /> },
};

const REGENERATE_OPTIONS = [
  { value: 'tone', label: 'Change Tone', description: 'More engaging and conversational' },
  { value: 'structure', label: 'Restructure', description: 'Better flow and readability' },
  { value: 'contrarian', label: 'More Contrarian', description: 'Provocative but professional' },
  { value: 'concise', label: 'Make Shorter', description: 'Keep the key message only' },
  { value: 'story', label: 'Add Story', description: 'Include personal anecdote' },
];

export function PostsPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Read URL params once on mount — no useSearchParams hook (avoids re-render cascades)
  const initialUrlParams = useRef(new URLSearchParams(window.location.search));

  const [posts, setPosts] = useState<PostDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>(() => initialUrlParams.current.get('source') || '');
  const [sourceItemFilter, setSourceItemFilter] = useState<string>('');
  const [sources, setSources] = useState<Source[]>([]);
  const [sourceItems, setSourceItems] = useState<Array<{ source_id: string; items: string[] }>>([]);

  // Generation from PostsPage
  const [generating, setGenerating] = useState(false);
  const cancelStreamRef = useRef<(() => void) | null>(null);

  // Editor state
  const [selectedPost, setSelectedPost] = useState<PostDraft | null>(null);
  const [loadingPostDetails, setLoadingPostDetails] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateMenu, setShowRegenerateMenu] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showKnowledgeInfo, setShowKnowledgeInfo] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Stats
  const [stats, setStats] = useState<Record<string, number>>({});


  // Weekly Insight
  const [weeklyInsight, setWeeklyInsight] = useState<WeeklyInsightType | null>(null);
  const [insightSelected, setInsightSelected] = useState(false);
  const [insightEditBody, setInsightEditBody] = useState('');
  const [acceptingInsight, setAcceptingInsight] = useState(false);

  // Recently generated post IDs — shown with a "NEW" badge
  const [newPostIds, setNewPostIds] = useState<Set<string>>(new Set());

  // LinkedIn promo
  const [linkedInStatus, setLinkedInStatus] = useState<LinkedInConnection | null>(null);
  const [linkedInPromoDismissed, setLinkedInPromoDismissed] = useState(
    () => localStorage.getItem('mazo_linkedin_promo_dismissed') === 'true'
  );

  // Derive display name and headline for LinkedIn preview
  const previewName = linkedInStatus?.displayName || user?.name || 'Your Name';
  const previewHeadline = linkedInStatus?.connected
    ? (linkedInStatus.email ? `${linkedInStatus.email}` : 'LinkedIn Member')
    : (user?.email || 'Your headline');

  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const initialLoadCalledRef = useRef(false);
  const statusFilterRef = useRef(statusFilter);
  const sourceFilterRef = useRef(sourceFilter);
  const sourceItemFilterRef = useRef(sourceItemFilter);
  const isMountedRef = useRef(true);
  const latestSelectIdRef = useRef<string | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any ongoing pipeline stream on unmount
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => { statusFilterRef.current = statusFilter; }, [statusFilter]);
  useEffect(() => { sourceFilterRef.current = sourceFilter; }, [sourceFilter]);
  useEffect(() => { sourceItemFilterRef.current = sourceItemFilter; }, [sourceItemFilter]);

  const loadPosts = useCallback(async () => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      clearCacheKey('posts-list');
      const result = await dedupeRequest('posts-list', () =>
        api.listPosts({
          status: statusFilterRef.current || undefined,
          source_id: sourceFilterRef.current || undefined,
          source_item_name: sourceItemFilterRef.current || undefined,
          limit: 50,
        })
      );
      if (result.data) {
        setPosts(result.data.data);
        setTotal(result.data.total);
      } else if (result.error) {
        setError(result.error.message);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      hasLoadedRef.current = true;
    }
  }, []);

  const loadStats = useCallback(async () => {
    const result = await dedupeRequest('posts-stats', () =>
      api.getPostStats()
    );
    if (result.data) {
      setStats(result.data.data.by_status);
    }
  }, []);

  // Initial load
  // Note: loadPosts and loadStats have stable references (empty deps), so we don't need them in the dependency array
  useEffect(() => {
    // Multiple guards to prevent repeated initial loads
    if (!isAuthenticated) return;
    if (hasLoadedRef.current) return;
    if (initialLoadCalledRef.current) return;

    // Mark as called BEFORE the async operations to prevent race conditions
    initialLoadCalledRef.current = true;

    loadPosts();
    loadStats();

    // Load sources for filter dropdown
    api.listSources().then(result => {
      if (result.data) {
        setSources(result.data.data);
      }
    });

    // Load source item names for sub-level filtering (calls/documents within a source)
    api.getPostSourceItems().then(result => {
      if (result.data) {
        const items = (result.data as any)?.data || result.data;
        if (Array.isArray(items)) {
          setSourceItems(items);
        }
      }
    }).catch(() => {});

    // Load weekly insight
    api.getWeeklyInsight().then(result => {
      if (result.data) {
        const insightData = (result.data as any)?.data ?? null;
        if (insightData && insightData.status === 'pending') {
          setWeeklyInsight(insightData);
        }
      }
    }).catch(() => {
      // Silently ignore -- weekly insight is non-critical
    });

    // Load LinkedIn status for promo banner and post preview name/headline
    api.getLinkedInStatus().then(result => {
      if (result.data) {
        const liData = (result.data as any)?.data || result.data;
        setLinkedInStatus(liData);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch full post details (knowledge items, versions) in background
  const fetchPostDetails = (post: PostDraft) => {
    const requestId = post.id;
    latestSelectIdRef.current = requestId;
    setLoadingPostDetails(true);
    clearCacheKey(`post-detail-${post.id}`);
    api.getPost(post.id).then(result => {
      if (latestSelectIdRef.current !== requestId || !isMountedRef.current) return;
      const postData = (result.data as any)?.data;
      if (postData) {
        setSelectedPost(postData);
        setEditBody(prev => prev === post.body ? postData.body : prev);
        setEditTitle(prev => prev === (post.title || '') ? (postData.title || '') : prev);
      }
    }).catch(() => {}).finally(() => {
      if (latestSelectIdRef.current === requestId) setLoadingPostDetails(false);
    });
  };

  // Auto-select first post when posts load and nothing is selected
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (posts.length > 0 && !selectedPost && !insightSelected && !hasAutoSelectedRef.current && hasLoadedRef.current) {
      // Check URL for a post to restore
      const urlPostId = initialUrlParams.current.get('post');
      const targetPost = urlPostId ? posts.find(p => p.id === urlPostId) : posts[0];
      if (targetPost) {
        hasAutoSelectedRef.current = true;
        setSelectedPost(targetPost);
        setEditBody(targetPost.body);
        setEditTitle(targetPost.title || '');
        fetchPostDetails(targetPost);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.length, selectedPost]);

  // Detect recently generated posts and mark them with "NEW" badges
  const hasCheckedNewPostsRef = useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current || posts.length === 0 || hasCheckedNewPostsRef.current) return;
    hasCheckedNewPostsRef.current = true;

    const generatedAt = sessionStorage.getItem('mazo_postsGeneratedAt');
    if (!generatedAt) return;

    const ts = parseInt(generatedAt, 10);
    // Only mark posts if generation happened within the last 10 minutes
    if (isNaN(ts) || Date.now() - ts > 10 * 60 * 1000) {
      sessionStorage.removeItem('mazo_postsGeneratedAt');
      return;
    }

    // Mark posts created after the generation timestamp (with a small buffer)
    const cutoff = new Date(ts - 30_000); // 30s buffer before generation started
    const recentIds = new Set(
      posts
        .filter(p => new Date(p.created_at) >= cutoff)
        .map(p => p.id)
    );

    if (recentIds.size > 0) {
      setNewPostIds(recentIds);
      sessionStorage.removeItem('mazo_postsGeneratedAt');

      // Auto-clear badges after 5 minutes
      const timer = setTimeout(() => {
        setNewPostIds(new Set());
      }, 5 * 60 * 1000);
      return () => clearTimeout(timer);
    } else {
      sessionStorage.removeItem('mazo_postsGeneratedAt');
    }
  }, [posts]);

  // Filter changes - only after initial load
  useEffect(() => {
    if (!isAuthenticated || !hasLoadedRef.current) return;

    const timer = setTimeout(() => {
      loadPosts();
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter, sourceItemFilter, isAuthenticated]);

  const selectInsight = () => {
    if (!weeklyInsight) return;
    setInsightSelected(true);
    setInsightEditBody(weeklyInsight.suggested_post);
    setSelectedPost(null);
    latestSelectIdRef.current = null;
    setShowKnowledgeInfo(false);
    setShowVersions(false);
  };

  const selectPost = (post: PostDraft) => {
    if (latestSelectIdRef.current === post.id) return;

    setInsightSelected(false);
    setSelectedPost(post);
    setEditBody(post.body);
    setEditTitle(post.title || '');
    setShowKnowledgeInfo(false);

    // Clear "NEW" badge when a post is selected
    if (newPostIds.has(post.id)) {
      setNewPostIds(prev => {
        const next = new Set(prev);
        next.delete(post.id);
        return next;
      });
    }

    // Update URL without triggering React re-renders
    const url = new URL(window.location.href);
    url.searchParams.set('post', post.id);
    window.history.replaceState(null, '', url.toString());

    fetchPostDetails(post);
  };

  const handleSave = async () => {
    if (!selectedPost) return;

    const hasBodyChanged = editBody !== selectedPost.body;
    const hasTitleChanged = editTitle !== (selectedPost.title || '');

    if (!hasBodyChanged && !hasTitleChanged) return;

    setSaving(true);
    try {
      const updateData: { body?: string; title?: string } = {};
      if (hasBodyChanged) updateData.body = editBody;
      if (hasTitleChanged) updateData.title = editTitle;

      const result = await api.updatePost(selectedPost.id, updateData);
      if (result.data) {
        setSelectedPost(result.data.data);
        setPosts(posts.map(p => p.id === selectedPost.id ? { ...p, ...updateData } : p));
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async (instruction: string) => {
    if (!selectedPost) return;

    setRegenerating(true);
    setShowRegenerateMenu(false);
    try {
      const result = await api.regeneratePost(selectedPost.id, {
        instruction: instruction as 'tone' | 'structure' | 'contrarian' | 'concise' | 'story',
      });
      if (result.data) {
        setSelectedPost(result.data.data);
        setEditBody(result.data.data.body);
        setEditTitle(result.data.data.title || '');
        setPosts(posts.map(p => p.id === selectedPost.id ? result.data!.data : p));
      } else if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate post');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedPost) return;
    await navigator.clipboard.writeText(selectedPost.body);
    // Show success toast
  };



  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    const result = await api.deletePost(id);
    if (!result.error) {
      // Immediately update UI
      setPosts(prevPosts => prevPosts.filter(p => p.id !== id));
      setTotal(prevTotal => Math.max(0, prevTotal - 1));
      if (selectedPost?.id === id) {
        setSelectedPost(null);
        latestSelectIdRef.current = null;
        // Clear URL param without triggering React re-renders
        const url = new URL(window.location.href);
        url.searchParams.delete('post');
        window.history.replaceState(null, '', url.toString());
      }
      // Clear the stats cache and refresh stats
      clearCacheKey('posts-stats');
      loadStats();
    } else {
      setError(result.error.message);
    }
  };

  const handleArchive = async (id: string) => {
    const result = await api.updatePost(id, { status: 'archived' });
    if (result.data) {
      setPosts(posts.map(p => p.id === id ? result.data!.data : p));
      if (selectedPost?.id === id) {
        setSelectedPost(result.data.data);
      }
      clearCacheKey('posts-stats');
      loadStats();
    } else if (result.error) {
      setError(result.error.message);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm('Are you sure you want to delete ALL posts? This action cannot be undone.')) return;
    if (!confirm('This will permanently delete all your posts. Are you really sure?')) return;

    setDeletingAll(true);
    setShowMoreMenu(false);

    // OPTIMISTIC UPDATE: Clear posts immediately for instant UI feedback
    const previousPosts = posts;
    const previousTotal = total;
    setPosts([]);
    setTotal(0);
    setSelectedPost(null);
    latestSelectIdRef.current = null;
    // Clear URL param without triggering React re-renders
    const url = new URL(window.location.href);
    url.searchParams.delete('post');
    window.history.replaceState(null, '', url.toString());
    clearCacheKey('posts-list');
    clearCacheKey('posts-stats');

    try {
      const result = await api.deleteAllPosts();
      if (result.error) {
        // Revert on error
        setPosts(previousPosts);
        setTotal(previousTotal);
        setError(result.error.message);
      } else {
        // Success - refresh stats
        loadStats();
      }
    } catch (err) {
      // Revert on error
      setPosts(previousPosts);
      setTotal(previousTotal);
      setError(err instanceof Error ? err.message : 'Failed to delete posts');
    }
    setDeletingAll(false);
  };

  const handleGeneratePosts = async () => {
    // If a source filter is active, generate posts from that specific source
    if (sourceFilter) {
      setGenerating(true);
      setError(null);

      try {
        const pipelineResult = await api.startPipeline(5, [sourceFilter]);

        if (pipelineResult.data?.pipelineId) {
          const pipelineId = pipelineResult.data.pipelineId;
          // Record generation timestamp for "NEW" badges
          sessionStorage.setItem('mazo_postsGeneratedAt', String(Date.now()));

          const cancel = await streamPipelineEvents(
            pipelineId,
            (progress: PipelineProgress) => {
              if (progress.phase === 'complete') {
                cancelStreamRef.current = null;
                setGenerating(false);
                clearCacheKey('posts-list');
                clearCacheKey('posts-stats');
                loadPosts();
                loadStats();
              } else if (progress.phase === 'failed') {
                cancelStreamRef.current = null;
                setError(progress.error || 'Generation failed');
                setGenerating(false);
              }
            },
            (err: string) => {
              cancelStreamRef.current = null;
              setError(err);
              setGenerating(false);
            },
          );

          cancelStreamRef.current = cancel;
          return;
        }

        // If pipeline returned error other than QUEUE_UNAVAILABLE, show error
        if (pipelineResult.error?.code !== 'QUEUE_UNAVAILABLE') {
          setError(pipelineResult.error?.message || 'Failed to start generation');
          setGenerating(false);
          return;
        }

        // Fallback to synchronous batch generation
        await api.syncSource(sourceFilter);
        const result = await api.generateBatchPosts(5, [sourceFilter]);
        if (result.error) {
          setError(result.error.message);
        } else {
          sessionStorage.setItem('mazo_postsGeneratedAt', String(Date.now()));
          clearCacheKey('posts-list');
          clearCacheKey('posts-stats');
          loadPosts();
          loadStats();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate posts');
      }
      setGenerating(false);
      return;
    }

    // No source filter — navigate to sources page for source selection
    navigate('/sources');
  };

  const handleAcceptInsight = async (insightId: string) => {
    setAcceptingInsight(true);
    try {
      const result = await api.acceptWeeklyInsight(insightId);
      if (result.data) {
        const acceptData = (result.data as any)?.data;
        if (acceptData?.post) {
          // If user edited the body in the insight editor, save it to the new post
          if (insightEditBody && insightEditBody !== weeklyInsight?.suggested_post) {
            await api.updatePost(acceptData.post.id, { body: insightEditBody });
            acceptData.post.body = insightEditBody;
          }
          // Add the new post to the list and select it
          setPosts(prev => [acceptData.post, ...prev]);
          setTotal(prev => prev + 1);
          setInsightSelected(false);
          selectPost(acceptData.post);
          clearCacheKey('posts-list');
          clearCacheKey('posts-stats');
          loadStats();
        }
        setWeeklyInsight(null);
      } else if (result.error) {
        setError(result.error.message);
      }
    } finally {
      setAcceptingInsight(false);
    }
  };

  const handleDismissInsight = async (insightId: string) => {
    const result = await api.dismissWeeklyInsight(insightId);
    if (!result.error) {
      setWeeklyInsight(null);
      setInsightSelected(false);
    } else {
      setError(result.error.message);
    }
  };

  const getCharCount = (text: string) => text.length;
  const getWordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-500">Please sign in to manage posts.</p>
      </div>
    );
  }

  return (
    <>
      {/* Loading overlay for post regeneration */}
      <LoadingOverlay
        isLoading={regenerating}
        title="Regenerating Post"
        description="AI is rewriting this post with your requested changes"
        statusText="Applying modifications..."
        icon="sparkles"
      />

      {/* Loading overlay for post generation from source filter */}
      <LoadingOverlay
        isLoading={generating}
        title="Generating Posts"
        description={`Creating posts from ${sources.find(s => s.id === sourceFilter)?.name || 'selected source'}`}
        statusText="Syncing source and generating content..."
        icon="sparkles"
      />

      <div className="flex h-[calc(100vh-8rem)]">
        {/* Posts list */}
        <div className="w-80 border-r bg-white flex flex-col">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-900">Posts</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleGeneratePosts}
                  disabled={generating}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-mazo-500 text-white text-sm rounded-lg hover:bg-mazo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={sourceFilter ? `Generate posts from ${sources.find(s => s.id === sourceFilter)?.name || 'selected source'}` : 'Go to Sources to generate posts'}
                >
                  {generating ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="h-3.5 w-3.5" />
                  )}
                  {generating ? 'Generating...' : 'Generate'}
                </button>
                {/* More menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                    title="More options"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {showMoreMenu && (
                    <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border py-1 z-10">
                      <button
                        onClick={handleDeleteAll}
                        disabled={deletingAll || posts.length === 0}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        {deletingAll ? 'Deleting...' : 'Delete All Posts'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

          {/* Stats bar */}
          <div className="flex items-center gap-3 mb-3 text-xs">
            <span className="text-gray-500">{total} total</span>
            <span className="text-gray-300">|</span>
            <span className="text-mazo-600">{stats.published || 0} published</span>
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Posts</option>
            <option value="ready_to_publish">Ready to Publish ({stats.ready_to_publish || 0})</option>
            <option value="published">Published ({stats.published || 0})</option>
            <option value="archived">Archived ({stats.archived || 0})</option>
          </select>

          {/* Source filter */}
          {sources.length > 0 && (
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                // Clear source item filter when source changes
                setSourceItemFilter('');
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm mt-2"
            >
              <option value="">All Sources</option>
              {sources.map(source => (
                <option key={source.id} value={source.id}>{source.name}</option>
              ))}
            </select>
          )}

          {/* Source item filter (calls/documents within selected source) */}
          {sourceFilter && (() => {
            const itemsForSource = sourceItems.find(si => si.source_id === sourceFilter);
            if (!itemsForSource || itemsForSource.items.length === 0) return null;
            return (
              <select
                value={sourceItemFilter}
                onChange={(e) => setSourceItemFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm mt-2"
              >
                <option value="">All items</option>
                {itemsForSource.items.map(itemName => (
                  <option key={itemName} value={itemName}>{itemName}</option>
                ))}
              </select>
            );
          })()}

          {/* Active filter chips */}
          {(sourceFilter || sourceItemFilter) && (() => {
            const sourceName = sources.find(s => s.id === sourceFilter)?.name || 'Unknown';
            const label = sourceItemFilter
              ? `${sourceName} > ${sourceItemFilter}`
              : sourceName;
            return (
              <div className="flex items-center gap-1.5 mt-2 px-2.5 py-1.5 bg-mazo-50 border border-mazo-200 rounded-lg text-xs text-mazo-700">
                <span className="truncate">Filtered by: {label}</span>
                <button
                  onClick={() => {
                    setSourceFilter('');
                    setSourceItemFilter('');
                  }}
                  className="p-0.5 hover:bg-mazo-100 rounded flex-shrink-0"
                  title="Clear filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })()}
        </div>

        {/* LinkedIn connect prompt */}
        {!linkedInPromoDismissed && linkedInStatus && !linkedInStatus.connected && (
          <div className="p-3 border-b">
            <div className="relative overflow-hidden rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-blue-50">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#0A66C2]" />
              <div className="p-3 flex items-center gap-3">
                <div className="p-1.5 bg-[#0A66C2] rounded-md flex-shrink-0">
                  <Linkedin className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900">New: Publish directly to LinkedIn</p>
                  <p className="text-xs text-gray-500">Connect your account in Settings to publish with one click.</p>
                </div>
                <button
                  onClick={() => navigate('/settings')}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0A66C2] text-white text-xs font-medium rounded-md hover:bg-[#094d92] transition-colors flex-shrink-0"
                >
                  Connect
                  <ArrowRight className="h-3 w-3" />
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('mazo_linkedin_promo_dismissed', 'true');
                    setLinkedInPromoDismissed(true);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors flex-shrink-0"
                  title="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-mazo-400" />
            </div>
          ) : posts.length === 0 && !weeklyInsight ? (
            <div className="p-4 text-center text-gray-500">
              <p>No posts yet.</p>
              <button
                onClick={() => navigate('/sources')}
                className="text-sm mt-1 text-mazo-500 hover:text-mazo-600 hover:underline"
              >
                Go to Sources to generate posts
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {/* Weekly Insight — pinned as first item in the list */}
              {weeklyInsight && (
                <div
                  onClick={selectInsight}
                  className={`p-3 cursor-pointer hover:bg-mazo-50/60 transition-colors border-l-2 border-l-mazo-500 ${
                    insightSelected ? 'bg-mazo-50' : 'bg-mazo-50/30'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-mazo-100 text-mazo-700">
                      Mazo Insight
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">
                    <span className="mr-1">✨</span>
                    {weeklyInsight.suggested_title || 'Insight of the Week'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">{weeklyInsight.suggested_post}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span>This week</span>
                    {weeklyInsight.knowledge_items && (
                      <>
                        <span>·</span>
                        <span className="truncate">Based on: {weeklyInsight.knowledge_items.title}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              {posts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => selectPost(post)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedPost?.id === post.id ? 'bg-mazo-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {newPostIds.has(post.id) && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-mazo-500 text-white">
                        NEW
                      </span>
                    )}
                    {post.publish_notes === 'Suggested by Mazo' && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-mazo-100 text-mazo-700">
                        Suggested by Mazo
                      </span>
                    )}
                    {(post.status === 'published' || post.status === 'ready_to_publish') && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${STATUS_CONFIG[post.status].color}`}>
                        {STATUS_CONFIG[post.status].icon}
                        {STATUS_CONFIG[post.status].label}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">v{post.current_version}</span>
                  </div>
                  {/* Post title */}
                  {post.title && (
                    <p className="text-sm font-medium text-gray-900 line-clamp-1 mb-0.5">{post.title}</p>
                  )}
                  {/* Post preview */}
                  <p className="text-sm text-gray-500 line-clamp-2">{post.body}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span>{getWordCount(post.body)} words</span>
                    <span>•</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Insight editor */}
        {insightSelected && weeklyInsight ? (
          <>
            {/* Insight banner */}
            <div className="px-6 py-3 bg-mazo-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="text-base">✨</span>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Mazo Insight of the Week</h3>
                  <p className="text-xs text-gray-500">Your best post opportunity this week</p>
                </div>
              </div>
              <button
                onClick={() => handleDismissInsight(weeklyInsight.id)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Dismiss insight"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Insight editor area */}
            <div className="flex-1 p-6 overflow-y-auto bg-gray-100">
              <div className="max-w-2xl mx-auto min-h-[400px]">
                {/* Knowledge source badge */}
                {weeklyInsight.knowledge_items && (
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-mazo-100 text-mazo-700">
                      {weeklyInsight.knowledge_items.type}
                    </span>
                    <span className="text-xs text-gray-500 truncate">
                      Based on: {weeklyInsight.knowledge_items.title}
                    </span>
                  </div>
                )}

                {/* Editable title */}
                {weeklyInsight.suggested_title && (
                  <p className="w-full mb-4 px-1 py-1.5 text-sm font-medium text-gray-500 border-b border-gray-300">
                    {weeklyInsight.suggested_title}
                  </p>
                )}

                {/* Editable post content in LinkedIn card style */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif' }}>
                  {/* LinkedIn profile header */}
                  <div className="px-4 pt-3 pb-2 flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mazo-400 to-mazo-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {previewName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">{previewName}</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">{previewHeadline}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-gray-400">Just now</span>
                        <span className="text-xs text-gray-400">·</span>
                        <Globe className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Editable post body */}
                  <div className="px-4 pb-2">
                    <textarea
                      value={insightEditBody}
                      onChange={(e) => setInsightEditBody(e.target.value)}
                      className="w-full min-h-[300px] p-0 border-0 bg-transparent focus:ring-0 focus:outline-none resize-y text-gray-900 placeholder:text-gray-400"
                      style={{ fontSize: '14px', lineHeight: '1.5' }}
                      placeholder="Edit this insight before saving..."
                    />
                  </div>

                  {/* Engagement stats (decorative) */}
                  <div className="px-4 py-1.5 flex items-center gap-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-0.5">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500">
                        <ThumbsUp className="h-2.5 w-2.5 text-white" />
                      </span>
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="mx-4 border-t border-gray-200" />

                  {/* LinkedIn action bar (decorative, non-interactive) */}
                  <div className="px-2 py-1 flex items-center justify-around pointer-events-none opacity-40">
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <ThumbsUp className="h-4 w-4" />
                      <span>Like</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <MessageCircle className="h-4 w-4" />
                      <span>Comment</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <Repeat2 className="h-4 w-4" />
                      <span>Repost</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <Send className="h-4 w-4" />
                      <span>Send</span>
                    </span>
                  </div>
                </div>

                {/* Stats row below the card */}
                <div className="flex items-center gap-3 mt-3 px-1 text-xs text-gray-400">
                  <span>{insightEditBody.trim().split(/\s+/).filter(Boolean).length} words</span>
                  <span>·</span>
                  <span>{insightEditBody.length} / 3,000 chars</span>
                </div>
              </div>
            </div>

            {/* Bottom bar with prominent Use This Post */}
            <div className="px-6 py-3 bg-white border-t flex items-center justify-between">
              <button
                onClick={() => handleDismissInsight(weeklyInsight.id)}
                className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Not this week
              </button>
              <button
                onClick={() => handleAcceptInsight(weeklyInsight.id)}
                disabled={acceptingInsight}
                className="inline-flex items-center gap-2 px-5 py-2 bg-mazo-500 text-white text-sm font-medium rounded-lg hover:bg-mazo-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {acceptingInsight ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Creating Post...
                  </>
                ) : (
                  <>
                    Use This Post
                    <ArrowRight className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            </div>
          </>
        ) : selectedPost ? (
          <>
            {/* Minimal toolbar */}
            <div className="px-6 py-3 bg-white border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedPost.status].color}`}>
                  {STATUS_CONFIG[selectedPost.status].icon}
                  {STATUS_CONFIG[selectedPost.status].label}
                </span>
                {(editBody !== selectedPost.body || editTitle !== (selectedPost.title || '')) && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">Unsaved</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setShowKnowledgeInfo(!showKnowledgeInfo); if (!showKnowledgeInfo) setShowVersions(false); }}
                  className={`p-2 rounded-lg ${showKnowledgeInfo ? 'text-mazo-500 bg-mazo-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                  title="Knowledge sources"
                >
                  <Info className="h-4 w-4" />
                </button>
                <button
                  onClick={() => { setShowVersions(!showVersions); if (!showVersions) setShowKnowledgeInfo(false); }}
                  className={`p-2 rounded-lg ${showVersions ? 'text-mazo-500 bg-mazo-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                  title="Version history"
                >
                  <History className="h-4 w-4" />
                </button>
                <button
                  onClick={handleCopy}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                  title="Copy to clipboard"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowRegenerateMenu(!showRegenerateMenu)}
                    disabled={regenerating}
                    className="p-2 text-gray-400 hover:text-mazo-400 hover:bg-mazo-50 rounded-lg disabled:opacity-50"
                    title="Regenerate"
                  >
                    {regenerating ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                  </button>
                  {showRegenerateMenu && (
                    <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border py-1 z-10">
                      {REGENERATE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleRegenerate(option.value)}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50"
                        >
                          <div className="font-medium text-gray-900 text-sm">{option.label}</div>
                          <div className="text-xs text-gray-500">{option.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* LinkedIn preview editor area */}
            <div className="flex-1 p-6 overflow-y-auto bg-gray-100">
              <div className="max-w-2xl mx-auto min-h-[400px]">
                {/* Editable title (for internal reference, outside the LinkedIn card) */}
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full mb-4 px-1 py-1.5 text-sm font-medium text-gray-500 bg-transparent border-0 border-b border-gray-300 focus:border-mazo-500 focus:ring-0 focus:outline-none placeholder:text-gray-400"
                  placeholder="Internal title (not shown on LinkedIn)"
                />

                {/* LinkedIn post card */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif' }}>
                  {/* LinkedIn profile header */}
                  <div className="px-4 pt-3 pb-2 flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mazo-400 to-mazo-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {previewName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 leading-tight">{previewName}</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">{previewHeadline}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-gray-400">Just now</span>
                        <span className="text-xs text-gray-400">·</span>
                        <Globe className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Post body textarea — styled to blend into the LinkedIn card */}
                  <div className="px-4 pb-2">
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      className="w-full min-h-[300px] p-0 border-0 bg-transparent focus:ring-0 focus:outline-none resize-y text-gray-900 placeholder:text-gray-400"
                      style={{ fontSize: '14px', lineHeight: '1.5' }}
                      placeholder="Write your post..."
                    />
                  </div>


                  {/* Engagement stats (decorative) */}
                  <div className="px-4 py-1.5 flex items-center gap-1 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-0.5">
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500">
                        <ThumbsUp className="h-2.5 w-2.5 text-white" />
                      </span>
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="mx-4 border-t border-gray-200" />

                  {/* LinkedIn action bar (decorative, non-interactive) */}
                  <div className="px-2 py-1 flex items-center justify-around pointer-events-none opacity-40">
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <ThumbsUp className="h-4 w-4" />
                      <span>Like</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <MessageCircle className="h-4 w-4" />
                      <span>Comment</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <Repeat2 className="h-4 w-4" />
                      <span>Repost</span>
                    </span>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-gray-500 rounded">
                      <Send className="h-4 w-4" />
                      <span>Send</span>
                    </span>
                  </div>
                </div>

                {/* Stats row below the card */}
                <div className="flex items-center gap-3 mt-3 px-1 text-xs text-gray-400">
                  <span>{getWordCount(editBody)} words</span>
                  <span>·</span>
                  <span>{getCharCount(editBody)} / 3,000 chars</span>
                  <span>·</span>
                  <span>v{selectedPost.current_version}</span>
                  {getCharCount(editBody) > 3000 && (
                    <>
                      <span>·</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        Exceeds LinkedIn 3,000 char limit
                      </span>
                    </>
                  )}
                </div>

                {/* Suggestions - alternative hooks only */}
                {selectedPost.hook_variants && selectedPost.hook_variants.length > 0 && (
                  <div className="mt-5 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Alternative Hooks</p>
                    <div className="space-y-1.5">
                      {selectedPost.hook_variants.slice(0, 3).map((hook, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            const lines = editBody.split('\n');
                            lines[0] = hook;
                            setEditBody(lines.join('\n'));
                          }}
                          className="w-full text-left p-2 text-xs text-gray-600 bg-gray-50 rounded-lg hover:bg-mazo-50 hover:text-mazo-700 transition-colors line-clamp-1"
                        >
                          {hook}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Simplified bottom bar */}
            <div className="px-6 py-3 bg-white border-t flex items-center justify-between">
              <div className="flex items-center gap-2">
                {selectedPost.status !== 'archived' && (
                  <button
                    onClick={() => handleArchive(selectedPost.id)}
                    className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                    title="Archive post"
                  >
                    <Archive className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedPost.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete post"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || (editBody === selectedPost.body && editTitle === (selectedPost.title || ''))}
                  className="px-4 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      // Save content + set status to ready_to_publish
                      const updateData: { body?: string; title?: string; status: 'ready_to_publish' } = { status: 'ready_to_publish' };
                      if (editBody !== selectedPost.body) updateData.body = editBody;
                      if (editTitle !== (selectedPost.title || '')) updateData.title = editTitle;
                      await api.updatePost(selectedPost.id, updateData);
                      // Navigate to publish page
                      navigate('/publish');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to publish');
                    }
                    setSaving(false);
                  }}
                  disabled={saving}
                  className="px-4 py-1.5 text-sm bg-mazo-500 text-white rounded-lg hover:bg-mazo-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <Edit2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p>Select a post to edit</p>
              <button
                onClick={() => navigate('/sources')}
                className="text-sm mt-1 text-mazo-500 hover:text-mazo-600 hover:underline"
              >
                Or go to Sources to generate new posts
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Knowledge sources sidebar */}
      {showKnowledgeInfo && selectedPost && (
        <div className="w-72 border-l bg-white overflow-y-auto flex-shrink-0">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Knowledge Sources</h3>
            <button onClick={() => setShowKnowledgeInfo(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          {loadingPostDetails ? (
            <div className="p-4 flex items-center gap-2">
              <div className="h-4 w-4 border-2 border-mazo-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Loading sources...</p>
            </div>
          ) : selectedPost.knowledge_items && selectedPost.knowledge_items.length > 0 ? (
            <div className="divide-y">
              {selectedPost.knowledge_items.map((item: any) => (
                <div key={item.id} className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-1.5 py-0.5 bg-mazo-100 text-mazo-700 rounded text-xs font-medium">{item.type}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">{item.title}</p>
                  {item.summary && (
                    <p className="text-xs text-gray-600 mb-1">{item.summary}</p>
                  )}
                  {item.snippet && (
                    <p className="text-xs text-gray-400 italic line-clamp-3 mt-1">"{item.snippet}"</p>
                  )}
                  {(item.source_item_name || item.source) && (
                    <p className="text-xs text-gray-400 mt-2">
                      {item.source_item_name
                        ? `From: ${item.source_item_name}`
                        : `Source: ${item.source?.name || 'Unknown'}`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4">
              <p className="text-sm text-gray-400">No knowledge items linked to this post.</p>
            </div>
          )}
        </div>
      )}

      {/* Version history sidebar */}
      {showVersions && selectedPost?.versions && (
        <div className="w-72 border-l bg-white overflow-y-auto">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Version History</h3>
            <button onClick={() => setShowVersions(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="divide-y">
            {selectedPost.versions.map((version) => (
              <div
                key={version.id}
                onClick={() => setEditBody(version.body)}
                className="p-3 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">v{version.version_number}</span>
                  <span className="text-xs text-gray-400">
                    {version.source === 'regenerate' ? 'AI' : 'Edit'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2">{version.body}</p>
                <span className="text-xs text-gray-400">
                  {new Date(version.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 shadow-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}
    </div>
    </>
  );
}
